import { Box, useToken } from '@chakra-ui/react'
import Error from 'next/error'
import { gql } from '@urql/core'
import { gqlErrorMessage } from 'lib/error'
import { WithLikedJobs } from 'hooks/useLikedJobs'
import useQuery from 'hooks/useQuery'
import {
    CompanyCard,
    CompanyCardData,
    FRAGMENT_COMPANY_CARD,
} from 'containers/company-jobs/components/CompanyCard'
import { Jobs } from 'containers/company-jobs/components/Jobs'
import { PageParams, usePageRouter } from 'containers/company-jobs/hooks/usePageRouter'
import AppShell from 'components/appShell'

export { PAGE_SIZE } from 'containers/company-jobs/components/Jobs'

const QUERY_COMPANY = gql<
    {
        company: CompanyCardData
    },
    { id?: string; slug: string }
>`
    ${FRAGMENT_COMPANY_CARD}
    query company($id: ID, $slug: String) {
        company(id: $id, slug: $slug) {
            ...CompanyCard
        }
    }
`

const CompanyJobsPageInner = ({
    params,
    toggleUnPublish,
    nextPageLink,
}: {
    params: PageParams
    toggleUnPublish: () => void
    nextPageLink: string
}) => {
    const [companyResult] = useQuery({
        query: QUERY_COMPANY,
        variables: {
            slug: params.companySlug,
        },
    })

    if (companyResult?.error) {
        return (
            <Error
                statusCode={
                    gqlErrorMessage(companyResult.error) === 'company-not-found'
                        ? 404
                        : 500
                }
            />
        )
    }

    const company = companyResult?.data?.company

    return (
        <Box
            mx={{
                base: '16px',
                sm: '24px',
                md: '32px',
                lg: '0',
            }}
            my={{ base: '16px', sm: '24px', md: '32px', lg: '48px', xl: '64px' }}
        >
            <CompanyCard company={company} />
            <Jobs
                company={company}
                params={params}
                toggleUnPublish={toggleUnPublish}
                nextPageLink={nextPageLink}
            />
        </Box>
    )
}

// defaultParams are used for testing in storybook to override hook values
export const CompanyJobsPage = ({ queryParams }: { queryParams?: PageParams }) => {
    const containerWidth = useToken('breakpoints', 'container1')
    const { params, toggleUnPublish, nextPageLink } = usePageRouter()
    return (
        <AppShell
            title={'Company Jobs Page'}
            contentMaxWidth={containerWidth}
            contentMinHeight="100vh"
            backgroundColor="gray.background"
        >
            <WithLikedJobs>
                <CompanyJobsPageInner
                    params={queryParams || params}
                    nextPageLink={nextPageLink}
                    toggleUnPublish={toggleUnPublish}
                />
            </WithLikedJobs>
        </AppShell>
    )
}
